import ApiService from './api.service';

export type UnreachablePolicy = 'skip' | 'deny';

/**
 * Limits the rows a group's members see, for the metrics it applies to.
 * `applies_to_metric_ids` empty = every metric connected to the dimension.
 * `unreachable`: what an applicable metric with no connection to the dimension
 * does — 'skip' shows it unrestricted, 'deny' hides it.
 */
export type RowRestriction = {
  dimension_id: string;
  comparator: string;
  value: string | string[] | null;
  applies_to_metric_ids: string[];
  unreachable: UnreachablePolicy;
};

export type UserGroup = {
  id: string;
  name: string;
  description: string;
  blacklisted_metric_ids: string[];
  row_restrictions: RowRestriction[];
  member_count: number;
};

export type UserGroupInput = Omit<UserGroup, 'id' | 'member_count'>;

/** Per metric; `absent` means the metric or dimension isn't in that branch. */
export type RestrictionStatus = 'applied' | 'skipped' | 'denied' | 'not_applicable' | 'absent';

/**
 * A restriction across Production and Develop. Scopes aren't branched and may only
 * reference published items: `breaks_on_publish` works now but won't after the next
 * publish, `broken` can't be enforced in Production (it's refused there).
 */
export type RestrictionState = 'live' | 'breaks_on_publish' | 'broken';

export type RestrictionCheck = { state: RestrictionState; message: string | null };

/** Where a metric or dimension stands between Production and Develop. */
export type CatalogItemState = 'published' | 'unpublished' | 'removed_in_develop';

export type CatalogDimension = {
  id: string;
  name: string;
  /** Set when it was renamed in Develop. */
  develop_name: string | null;
  state: CatalogItemState;
};

export type CatalogMetric = CatalogDimension & {
  is_calculation: boolean;
  /** Calculated metrics: the non-calculated metrics it's built from, at any depth. */
  base_metric_ids: string[];
  /** Dimension ids it can be broken down by in Production, and in Develop. */
  connected_now: string[];
  connected_after_publish: string[];
};

/** Metrics and dimensions from both branches, for the scope editors. */
export type ScopeCatalog = {
  has_draft: boolean;
  dimensions: CatalogDimension[];
  metrics: CatalogMetric[];
};

export type SaveGroupResult = { record: UserGroup; restriction_checks: RestrictionCheck[] };

export type CoverageWarning = {
  level: 'error' | 'warning';
  message: string;
  group_id?: string;
};

export type GroupCoverage = {
  id: string;
  name: string;
  member_count: number;
  hidden_metric_ids: string[];
  restrictions: {
    description: string;
    state: RestrictionState;
    /** Production. */
    statuses: Record<string, RestrictionStatus>;
    /** Develop; null when there's no unpublished draft. */
    after_publish: Record<string, RestrictionStatus> | null;
  }[];
};

export type Coverage = {
  has_draft: boolean;
  metrics: { id: string; name: string; state: CatalogItemState }[];
  groups: GroupCoverage[];
  warnings: CoverageWarning[];
};

/** How publishing Develop changes a scoped group's or user's access (publish dialog). */
export type AccessChanges = {
  subjects: {
    kind: 'group' | 'user';
    id: string;
    /** Null for a user: look the name up from the tenant's users. */
    name: string | null;
    member_count: number | null;
    items: {
      level: 'info' | 'warning' | 'error';
      /** Full text. */
      message: string;
      /** When the line lists metrics: the text with `{metrics}` where they go, and their names. */
      template?: string;
      metrics?: string[];
    }[];
  }[];
  requires_acknowledgement: boolean;
  /** Identifies this exact list; sent back with the publish to acknowledge it. */
  digest: string | null;
};

export class UserGroupService {
  async list(): Promise<UserGroup[]> {
    const result = await ApiService.getInstance().request('GET', '/user-groups') as { records: UserGroup[] };
    return result.records;
  }

  /** frontegg_id -> group_id for every user in a group. */
  async memberships(): Promise<Record<string, string>> {
    const result = await ApiService.getInstance().request('GET', '/user-groups/memberships') as { records: Record<string, string> };
    return result.records;
  }

  async coverage(): Promise<Coverage> {
    return await ApiService.getInstance().request('GET', '/user-groups/coverage') as Coverage;
  }

  async catalog(): Promise<ScopeCatalog> {
    return await ApiService.getInstance().request('GET', '/user-groups/catalog') as ScopeCatalog;
  }

  async create(group: UserGroupInput): Promise<SaveGroupResult> {
    return await ApiService.getInstance().request('POST', '/user-groups', group) as SaveGroupResult;
  }

  async update(id: string, group: UserGroupInput): Promise<SaveGroupResult> {
    return await ApiService.getInstance().request('PUT', `/user-groups/${id}`, group) as SaveGroupResult;
  }

  async remove(id: string): Promise<void> {
    await ApiService.getInstance().request('DELETE', `/user-groups/${id}`);
  }
}

export const userGroupService = new UserGroupService();
